/**
 * @file session-signal.ts
 * @description "Is there a live end-user session right now?", readable from a
 * non-React context.
 *
 * Clerk is the source of truth, but the error handler is a plain module called from
 * React Query's `QueryCache`/`MutationCache` — no hooks, no context. This is the same
 * bridge shape `common/services/auth.ts` already uses for `getToken`: a component that
 * DOES have Clerk context installs a reader, and non-React code reads through it.
 *
 * WHY IT IS A TRI-STATE, AND WHY `unknown` IS NOT `anonymous`.
 * A probe that nobody installed must never be mistaken for "there is no session". That
 * failure is byte-identical to the bug this exists to fix: the signal quietly reads
 * "logged out", 401s go back to being suppressed, and nothing anywhere is red. So the
 * unwired state is its own value, every consumer must decide what to do with it, and
 * `reportError` deliberately resolves it TOWARDS telemetry and tags the Sentry event
 * `session: unknown`. A wiring regression therefore shows up as a burst of events
 * carrying that tag, instead of as silence.
 *
 * @see AppProviders.tsx — installs the Clerk-backed reader, and the anonymous one for embeds
 * @see error-handler.ts — `isReportable`, the one consumer of this signal
 */

/**
 * What the app knows about the current end-user session.
 *
 * - `authenticated` — Clerk has loaded and holds a live session.
 * - `anonymous` — Clerk has loaded and there is no session (signed out, or an embed).
 * - `unknown` — Clerk has not loaded yet, or nothing installed a reader at all.
 */
export type SessionSignal = "authenticated" | "anonymous" | "unknown"

let read: (() => SessionSignal) | null = null

/**
 * Installs the reader used by {@link readSessionSignal}. Call from a component that has
 * Clerk context (or that knows the surface is anonymous, like the embed).
 *
 * @returns an uninstaller, so this composes directly with `useEffect`'s cleanup. It only
 * uninstalls the reader it installed, so a re-install from a newer render is never undone
 * by an older effect's cleanup.
 */
export function installSessionSignal(reader: () => SessionSignal): () => void {
  read = reader
  return () => {
    if (read === reader) read = null
  }
}

/** Reads the current session state. Returns `unknown` when nothing is installed. */
export function readSessionSignal(): SessionSignal {
  return read?.() ?? "unknown"
}
